import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import {
  Calendar, TrendingUp, Download, RefreshCw, Loader2, Truck, Users,
  MapPin, DollarSign, Clock, Target, FileText, BarChart3, PieChart as PieChartIcon,
  Activity, Award, AlertTriangle, CheckCircle, XCircle, Timer, ChevronDown,
  ChevronRight, Maximize2, Grid3X3, TrendingUpIcon, Brain, Eye, LayoutDashboard
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel, prepareExportData } from './reportUtils';
import { FlexibleReportsBuilder } from './FlexibleReportsBuilder';
import { API_CONFIG, getAuthHeaders } from '../config/api';
import { driverService } from '../services/driverService';

const API_BASE = API_CONFIG.BASE_URL.replace(/\/$/, '').replace(/\/api$/, '');

const reportCategories = [
  { id: 'dashboard', name: 'Dashboard Özeti', icon: LayoutDashboard, color: '#6366F1' },
  { id: 'comparison', name: 'Dönem Karşılaştırma', icon: TrendingUpIcon, color: '#8B5CF6' },
  { id: 'predictive', name: 'Tahmin & Analitik', icon: Brain, color: '#EC4899' },
  { id: 'operation', name: 'Operasyon Özeti', icon: Activity, color: '#3B82F6' },
  { id: 'financial', name: 'Finansal Rapor', icon: DollarSign, color: '#10B981' },
  { id: 'driver', name: 'Sürücü Performans', icon: Users, color: '#8B5CF6' },
  { id: 'harcirah', name: 'Harcırah', icon: DollarSign, color: '#0F766E' },
  { id: 'vehicle', name: 'Araç Performans', icon: Truck, color: '#F59E0B' },
  { id: 'customer', name: 'Müşteri Analizi', icon: MapPin, color: '#EC4899' },
  { id: 'route', name: 'Güzergah Analizi', icon: PieChartIcon, color: '#06B6D4' },
  { id: 'bid', name: 'Teklif Analizi', icon: Target, color: '#F97316' },
  { id: 'ontime', name: 'Zamanında Teslimat', icon: Clock, color: '#14B8A6' },
  { id: 'accounting', name: 'Muhasebe & Ödeme', icon: FileText, color: '#6366F1' },
  { id: 'waiting', name: 'Bekleme Süreleri', icon: Timer, color: '#EF4444' },
  { id: 'custom', name: 'Özel Rapor Oluşturucu', icon: Grid3X3, color: '#64748B' },
];

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1'];
const PIE_COLORS = ['#60A5FA', '#34D399', '#FCD34D', '#A78BFA', '#F87171', '#FBBF24', '#38BDF8', '#FB923C', '#2DD4BF', '#818CF8'];

function StatCard({ title, value, subtitle, icon: Icon, color, trend, onClick }: any) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2.5 rounded-lg`} style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        {trend !== undefined && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-50 text-green-700' : trend < 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-500'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('tr-TR').format(value);
}

function formatDateTime(value: any): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatDurationParts(value: any): string {
  const totalSeconds = Number(value || 0);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '-';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const seconds = totalSeconds % 60;
  return `${days}g ${hours}s ${seconds}sn`;
}

function toNumeric(value: any): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function EmptyReportState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
      <p className="text-base font-semibold text-slate-700">{label}</p>
      <p className="mt-2 text-sm text-slate-500">Bu endpoint veri donmedi veya mevcut filtrelerle kayit bulunamadi.</p>
    </div>
  );
}

function DrillDownModal({ title, data, columns, onClose }: any) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            {title}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-auto flex-1 p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                {columns.map((c: any) => (
                  <th key={c.key} className="text-left p-3 font-medium text-gray-600">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row: any, i: number) => (
                <tr key={i} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  {columns.map((c: any) => (
                    <td key={c.key} className="p-3">{c.render ? c.render(row[c.key], row) : row[c.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CityHeatmap({ data, title }: any) {
  if (!data || !data.length) return <div className="text-center text-gray-400 py-12">Veri yok</div>;
  const maxVal = Math.max(...data.map((d: any) => d.value));
  const getColor = (val: number) => {
    const ratio = val / maxVal;
    if (ratio > 0.8) return '#1E40AF';
    if (ratio > 0.6) return '#3B82F6';
    if (ratio > 0.4) return '#60A5FA';
    if (ratio > 0.2) return '#93C5FD';
    return '#DBEAFE';
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
        {data.map((item: any, i: number) => (
          <div
            key={i}
            className="rounded-lg p-3 text-center transition-transform hover:scale-105"
            style={{ backgroundColor: getColor(item.value), color: item.value / maxVal > 0.4 ? '#fff' : '#1E40AF' }}
            title={`${item.name}: ${formatNumber(item.value)}`}
          >
            <p className="text-xs font-medium truncate">{item.name}</p>
            <p className="text-lg font-bold">{formatNumber(item.value)}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
        <span className="text-xs text-gray-500">Düşük</span>
        <div className="flex gap-1">
          {['#DBEAFE', '#93C5FD', '#60A5FA', '#3B82F6', '#1E40AF'].map((c, i) => (
            <div key={i} className="w-8 h-3 rounded" style={{ backgroundColor: c }} />
          ))}
        </div>
        <span className="text-xs text-gray-500">Yüksek</span>
      </div>
    </div>
  );
}

// ===================== DASHBOARD SUMMARY =====================
function DashboardSummary({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/dashboard-summary?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) {
      toast.error('Dashboard özeti yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Dashboard ozeti verisi bulunamadi" />;
  const { kpis, revenueByModel, topCities, fleetUtilization, pendingBids, pendingPayments } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard title="Toplam Sevkiyat" value={formatNumber(kpis.total_shipments)} icon={BarChart3} color="#3B82F6" />
        <StatCard title="Teslimat Oranı" value={kpis.total_shipments > 0 ? `${((kpis.delivered / kpis.total_shipments) * 100).toFixed(1)}%` : '0%'} icon={CheckCircle} color="#10B981" />
        <StatCard title="Toplam Gelir" value={formatCurrency(kpis.total_revenue)} icon={DollarSign} color="#F59E0B" />
        <StatCard title="Ort. TL/KM" value={formatCurrency(Math.round(kpis.avg_tl_km))} icon={TrendingUp} color="#8B5CF6" />
        <StatCard title="Aktif Sürücü" value={formatNumber(kpis.active_drivers)} icon={Users} color="#EC4899" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <p className="text-sm opacity-80">Özmal Gelir</p>
          <p className="text-2xl font-bold">{formatCurrency(revenueByModel.find((r: any) => r.transport_model === 'own-fleet')?.revenue || 0)}</p>
          <p className="text-xs opacity-60 mt-1">{formatNumber(revenueByModel.find((r: any) => r.transport_model === 'own-fleet')?.count || 0)} sefer</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <p className="text-sm opacity-80">Spot Gelir</p>
          <p className="text-2xl font-bold">{formatCurrency(revenueByModel.find((r: any) => r.transport_model === 'spot')?.revenue || 0)}</p>
          <p className="text-xs opacity-60 mt-1">{formatNumber(revenueByModel.find((r: any) => r.transport_model === 'spot')?.count || 0)} sefer</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <p className="text-sm opacity-80">Bekleyen Ödemeler</p>
          <p className="text-2xl font-bold">{formatCurrency(pendingPayments.total_value)}</p>
          <p className="text-xs opacity-60 mt-1">{formatNumber(pendingPayments.count)} sevk</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CityHeatmap data={topCities.map((c: any) => ({ name: c.city, value: c.shipment_count }))} title="Şehir Isı Haritası (Sevkiyat)" />
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filo Kullanım Oranı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={fleetUtilization}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="vehicle_type" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_vehicles" fill="#94A3B8" name="Toplam Araç" radius={[4, 4, 0, 0]} />
              <Bar dataKey="used_vehicles" fill="#3B82F6" name="Kullanılan" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gelir Dağılımı (Model Bazında)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={revenueByModel.map((r: any) => ({ name: r.transport_model === 'own-fleet' ? 'Özmal' : r.transport_model === 'spot' ? 'Spot' : r.transport_model, value: r.revenue }))} cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} dataKey="value">
              {revenueByModel.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: number) => formatCurrency(v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ===================== COMPARISON REPORT =====================
function ComparisonReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setData({ loading: false, needsDates: true });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/comparison?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) {
      toast.error('Karşılaştırma raporu yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Donem karsilastirma verisi bulunamadi" />;
  if ((data as any).needsDates) return <div className="bg-white rounded-xl shadow-sm border p-12 text-center"><Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Karşılaştırma için tarih aralığı seçin</p></div>;
  const { comparisons, dailyComparison, previousDaily, periodInfo } = data;
  const mergedDaily = dailyComparison.map((d: any) => {
    const prev = previousDaily.find((p: any) => p.date === d.date);
    return { date: d.date, current: d.current_count, previous: prev?.previous_count || 0, currentRevenue: d.current_revenue, previousRevenue: prev?.previous_revenue || 0 };
  });
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 flex items-center gap-3">
        <Calendar className="w-5 h-5 text-blue-600 shrink-0" />
        <p className="text-sm text-blue-800"><strong>{periodInfo.current.start} → {periodInfo.current.end}</strong> karşı <strong>{periodInfo.previous.start} → {periodInfo.previous.end}</strong> dönemi</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {comparisons.map((c: any) => {
          const isGood = c.metric.includes('İptal') || c.metric.includes('Bekleme') || c.metric.includes('Gider') ? c.change <= 0 : c.change >= 0;
          return (<StatCard key={c.metric} title={c.metric} value={c.metric.includes('Gelir') || c.metric.includes('Gider') || c.metric.includes('TL') || c.metric.includes('Bekleme') ? formatCurrency(c.current) : formatNumber(c.current)} icon={isGood ? CheckCircle : AlertTriangle} color={isGood ? '#10B981' : '#EF4444'} trend={c.change} />);
        })}
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Günlük Karşılaştırma - Sevkiyat</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={mergedDaily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="current" fill="#3B82F6" name="Bu Dönem" radius={[4, 4, 0, 0]} />
            <Bar dataKey="previous" fill="#CBD5E1" name="Önceki Dönem" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detaylı Karşılaştırma Tablosu</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Metrik</th><th className="text-center p-3 font-medium text-gray-600">Bu Dönem</th><th className="text-center p-3 font-medium text-gray-600">Önceki Dönem</th><th className="text-center p-3 font-medium text-gray-600">Değişim</th></tr></thead>
          <tbody>
            {comparisons.map((c: any, i: number) => (<tr key={i} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}><td className="p-3 font-medium text-gray-900">{c.metric}</td><td className="p-3 text-center">{c.metric.includes('Gelir') || c.metric.includes('Gider') || c.metric.includes('TL') || c.metric.includes('Bekleme') ? formatCurrency(c.current) : formatNumber(c.current)}</td><td className="p-3 text-center">{c.metric.includes('Gelir') || c.metric.includes('Gider') || c.metric.includes('TL') || c.metric.includes('Bekleme') ? formatCurrency(c.previous) : formatNumber(c.previous)}</td><td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.change > 0 ? 'bg-green-100 text-green-700' : c.change < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{c.change > 0 ? '+' : ''}{c.change}%</span></td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== PREDICTIVE ANALYTICS =====================
function PredictiveAnalytics({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/predictive?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) {
      toast.error('Tahmin analizi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Tahmin analizi verisi bulunamadi" />;
  const { monthlyData, forecasts, healthScore, revenueByDayOfWeek } = data;
  const getHealthLabel = (score: number) => {
    if (score >= 90) return { label: 'Mükemmel', color: '#10B981' };
    if (score >= 75) return { label: 'İyi', color: '#3B82F6' };
    if (score >= 60) return { label: 'Orta', color: '#F59E0B' };
    return { label: 'Zayıf', color: '#EF4444' };
  };
  const health = getHealthLabel(healthScore);
  const forecastChartData = monthlyData.map((m: any) => ({ month: m.month, shipments: m.shipment_count, revenue: m.revenue, forecastShipments: null, forecastRevenue: null }));
  if (forecasts) {
    forecasts.months.forEach((month: string, i: number) => {
      forecastChartData.push({ month, shipments: null, revenue: null, forecastShipments: forecasts.shipmentCounts[i], forecastRevenue: forecasts.revenues[i] });
    });
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <p className="text-sm text-gray-500 mb-1">Sistem Sağlık Skoru</p>
          <div className="flex items-center gap-3">
            <p className="text-3xl font-bold" style={{ color: health.color }}>{healthScore}%</p>
            <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${health.color}15`, color: health.color }}>{health.label}</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full mt-2"><div className="h-full rounded-full transition-all" style={{ width: `${healthScore}%`, backgroundColor: health.color }} /></div>
        </div>
        <StatCard title="Gelecek Ay Tahmini Sevkiyat" value={forecasts ? formatNumber(forecasts.shipmentCounts[0]) : '-'} icon={TrendingUp} color="#3B82F6" />
        <StatCard title="Gelecek Ay Gelir Tahmini" value={forecasts ? formatCurrency(forecasts.revenues[0]) : '-'} icon={DollarSign} color="#10B981" />
        <StatCard title="Trend" value={forecasts && forecasts.shipmentCounts[0] > (monthlyData[monthlyData.length - 1]?.shipment_count || 0) ? 'Yükseliş' : 'Düşüş'} icon={Brain} color="#8B5CF6" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sevkiyat Trendi & Tahmin</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={forecastChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Legend />
              <Bar dataKey="shipments" fill="#3B82F6" name="Gerçekleşen" radius={[4, 4, 0, 0]} />
              <Bar dataKey="forecastShipments" fill="#93C5FD" name="Tahmin" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gelir Trendi & Tahmin</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={forecastChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}K`} /><Tooltip formatter={(v: number) => formatCurrency(v)} /><Legend />
              <Bar dataKey="revenue" fill="#10B981" name="Gerçekleşen" radius={[4, 4, 0, 0]} />
              <Bar dataKey="forecastRevenue" fill="#6EE7B7" name="Tahmin" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {revenueByDayOfWeek && revenueByDayOfWeek.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Güne Göre Sevkiyat Dağılımı (Son 3 Ay)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueByDayOfWeek}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="day_name" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Bar dataKey="count" fill="#F59E0B" name="Sevkiyat Sayısı" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Aylık Veri Geçmişi</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Ay</th><th className="text-center p-3 font-medium text-gray-600">Sevkiyat</th><th className="text-center p-3 font-medium text-gray-600">Gelir</th><th className="text-center p-3 font-medium text-gray-600">Ort. Mesafe</th><th className="text-center p-3 font-medium text-gray-600">Aktif Sürücü</th></tr></thead>
          <tbody>
            {monthlyData.map((m: any, i: number) => (<tr key={i} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}><td className="p-3 font-medium text-gray-900">{m.month}</td><td className="p-3 text-center">{formatNumber(m.shipment_count)}</td><td className="p-3 text-center">{formatCurrency(m.revenue)}</td><td className="p-3 text-center">{Math.round(m.avg_distance)} km</td><td className="p-3 text-center">{formatNumber(m.active_drivers)}</td></tr>))}
            {forecasts && forecasts.months.map((month: string, i: number) => (<tr key={i} className="border-b bg-blue-50"><td className="p-3 font-medium text-blue-800">{month} (Tahmin)</td><td className="p-3 text-center text-blue-600">{formatNumber(forecasts.shipmentCounts[i])}</td><td className="p-3 text-center text-blue-600">{formatCurrency(forecasts.revenues[i])}</td><td className="p-3 text-center text-gray-400">-</td><td className="p-3 text-center text-gray-400">-</td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== OPERATION SUMMARY =====================
function OperationSummary({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [drillDown, setDrillDown] = useState<any>(null);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/operation-summary?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Operasyon raporu yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Operasyon ozeti verisi bulunamadi" />;
  const { summary, dailyTrend, stageDistribution, loadTypeDistribution } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard title="Toplam Sevkiyat" value={formatNumber(summary.total_shipments)} icon={BarChart3} color="#3B82F6" />
        <StatCard title="Teslim Edildi" value={formatNumber(summary.delivered)} icon={CheckCircle} color="#10B981" />
        <StatCard title="Yolda" value={formatNumber(summary.in_transit)} icon={Truck} color="#F59E0B" />
        <StatCard title="Atandı" value={formatNumber(summary.assigned)} icon={Users} color="#8B5CF6" />
        <StatCard title="Toplam Gelir" value={formatCurrency(summary.total_revenue)} icon={DollarSign} color="#10B981" />
        <StatCard title="Ort. Mesafe" value={`${formatNumber(Math.round(summary.avg_distance))} km`} icon={MapPin} color="#06B6D4" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Günlük Sevkiyat Trendi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={[...dailyTrend].reverse()}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Area type="monotone" dataKey="count" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.15} name="Toplam" /><Area type="monotone" dataKey="delivered" stroke="#10B981" fill="#10B981" fillOpacity={0.15} name="Teslim" /></AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Aşama Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stageDistribution}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="stage_name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={80} /><YAxis /><Tooltip /><Bar dataKey="count" name="Adet" fill="#3B82F6" radius={[4, 4, 0, 0]} onClick={() => setDrillDown({ type: 'stage' })} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Yük Türü Dağılımı</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart><Pie data={loadTypeDistribution} cx="50%" cy="50%" outerRadius={90} label={({ load_type, percent }) => `${load_type || 'Belirtilmemiş'} ${(percent * 100).toFixed(0)}%`} dataKey="count">{loadTypeDistribution.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Genel İstatistikler</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">Aktif Sürücü</span><span className="font-semibold text-gray-900">{summary.active_drivers}</span></div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">Aktif Araç</span><span className="font-semibold text-gray-900">{summary.active_vehicles}</span></div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">İptal Oranı</span><span className="font-semibold text-red-600">{summary.total_shipments > 0 ? ((summary.cancelled / summary.total_shipments) * 100).toFixed(1) : 0}%</span></div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">Teslimat Oranı</span><span className="font-semibold text-green-600">{summary.total_shipments > 0 ? ((summary.delivered / summary.total_shipments) * 100).toFixed(1) : 0}%</span></div>
          </div>
        </div>
      </div>
      {drillDown && <DrillDownModal title="Aşama Detayı" data={stageDistribution} columns={[{ key: 'stage_name', label: 'Aşama' }, { key: 'count', label: 'Adet' }]} onClose={() => setDrillDown(null)} />}
    </div>
  );
}

// ===================== FINANCIAL REPORT =====================
function FinancialReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/financial?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Finansal rapor yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Finansal rapor verisi bulunamadi" />;
  const { financials, expenses, monthlyRevenue, carrierTypeBreakdown } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Navlun" value={formatCurrency(financials.total_navlun)} icon={DollarSign} color="#10B981" />
        <StatCard title="Toplam Komisyon" value={formatCurrency(financials.total_komisyon)} icon={FileText} color="#8B5CF6" />
        <StatCard title="Ort. TL/KM" value={formatCurrency(Math.round(financials.avg_tl_km))} icon={TrendingUp} color="#F59E0B" />
        <StatCard title="Ort. Navlun" value={formatCurrency(Math.round(financials.avg_navlun))} icon={Truck} color="#3B82F6" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5"><p className="text-sm text-gray-500 mb-1">Özmal Gelir</p><p className="text-xl font-bold text-blue-600">{formatCurrency(financials.ozmal_gelir)}</p></div>
        <div className="bg-white rounded-xl shadow-sm border p-5"><p className="text-sm text-gray-500 mb-1">Spot Gelir</p><p className="text-xl font-bold text-orange-600">{formatCurrency(financials.spot_gelir)}</p></div>
        <div className="bg-white rounded-xl shadow-sm border p-5"><p className="text-sm text-gray-500 mb-1">Teklif Geliri</p><p className="text-xl font-bold text-purple-600">{formatCurrency(financials.bid_gelir)}</p></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Aylık Gelir Trendi</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={[...monthlyRevenue].reverse()}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}K`} /><Tooltip formatter={(v: number) => formatCurrency(v)} /><Bar dataKey="revenue" fill="#10B981" name="Gelir" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Taşıyıcı Tipi Bazında</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={carrierTypeBreakdown}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="carrier_type" tick={{ fontSize: 11 }} /><YAxis /><Tooltip formatter={(v: number) => formatCurrency(v)} /><Bar dataKey="total_amount" fill="#3B82F6" name="Toplam" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gider Dağılımı</h3>
          {expenses.length > 0 ? (<ResponsiveContainer width="100%" height={280}><PieChart><Pie data={expenses} cx="50%" cy="50%" outerRadius={90} label={({ category_name, percent }) => `${category_name} ${(percent * 100).toFixed(0)}%`} dataKey="category_total">{expenses.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip formatter={(v: number) => formatCurrency(v)} /></PieChart></ResponsiveContainer>) : <p className="text-center text-gray-400 py-20">Gider verisi yok</p>}
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ek Ödeme Kalemleri</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"><span className="text-orange-700">Uğrama Bedeli</span><span className="font-bold text-orange-700">{formatCurrency(financials.total_ugrama)}</span></div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg"><span className="text-red-700">Bekleme Bedeli</span><span className="font-bold text-red-700">{formatCurrency(financials.total_bekleme)}</span></div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg"><span className="text-green-700">Genel Toplam</span><span className="font-bold text-green-700">{formatCurrency(financials.total_gelir)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== DRIVER PERFORMANCE =====================
function DriverPerformanceReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [drillDown, setDrillDown] = useState<any>(null);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/driver-performance?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Sürücü raporu yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Surucu performans verisi bulunamadi" />;
  const { driverStats, deliveryTimeStats, topDrivers } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Sürücü" value={formatNumber(driverStats.filter((d: any) => d.total_shipments > 0).length)} icon={Users} color="#8B5CF6" />
        <StatCard title="En Çok Teslimat" value={driverStats[0]?.full_name || '-'} subtitle={`${formatNumber(driverStats[0]?.delivered || 0)} teslimat`} icon={Award} color="#F59E0B" />
        <StatCard title="Ort. Teslimat Süresi" value={`${toNumeric(deliveryTimeStats[0]?.avg_delivery_hours).toFixed(1)} saat`} icon={Clock} color="#14B8A6" />
        <StatCard title="En Yüksek Gelir" value={formatCurrency(topDrivers[0]?.revenue || 0)} subtitle={topDrivers[0]?.full_name} icon={DollarSign} color="#10B981" />
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sürücü Performans Tablosu</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Sürücü</th><th className="text-center p-3 font-medium text-gray-600">Şehir</th><th className="text-center p-3 font-medium text-gray-600">Teslimat</th><th className="text-center p-3 font-medium text-gray-600">İptal</th><th className="text-center p-3 font-medium text-gray-600">Toplam KM</th><th className="text-center p-3 font-medium text-gray-600">Gelir</th><th className="text-center p-3 font-medium text-gray-600">TL/KM</th><th className="text-center p-3 font-medium text-gray-600">Puan</th></tr></thead>
            <tbody>
              {driverStats.filter((d: any) => d.total_shipments > 0).map((d: any, i: number) => (
                <tr key={d.id} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'} cursor-pointer hover:bg-blue-50`} onClick={() => setDrillDown({ type: 'driver', data: d })}>
                  <td className="p-3 font-medium text-gray-900">{d.full_name}</td>
                  <td className="p-3 text-center text-gray-500">{d.city || '-'}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">{d.delivered}</span></td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">{d.cancelled}</span></td>
                  <td className="p-3 text-center">{formatNumber(Math.round(d.total_km))} km</td>
                  <td className="p-3 text-center font-medium">{formatCurrency(d.total_revenue)}</td>
                  <td className="p-3 text-center">{formatCurrency(Math.round(d.avg_tl_km))}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${Number(d.rating) >= 4.5 ? 'bg-green-100 text-green-700' : Number(d.rating) >= 4 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{Number(d.rating).toFixed(1)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">En Çok Gelir Getiren Sürücüler</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={topDrivers} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis type="number" tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}K`} /><YAxis type="category" dataKey="full_name" width={120} tick={{ fontSize: 11 }} /><Tooltip formatter={(v: number) => formatCurrency(v)} /><Bar dataKey="revenue" fill="#10B981" name="Gelir" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ortalama Teslimat Süreleri</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={deliveryTimeStats.slice(0, 10)}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="full_name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={80} /><YAxis label={{ value: 'Saat', angle: -90, position: 'insideLeft' }} /><Tooltip /><Bar dataKey="avg_delivery_hours" fill="#14B8A6" name="Ort. Süre (saat)" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      {drillDown?.type === 'driver' && (<DrillDownModal title={`Sürücü Detayı: ${drillDown.data.full_name}`} data={[drillDown.data]} columns={[{ key: 'full_name', label: 'Sürücü' }, { key: 'phone', label: 'Telefon' }, { key: 'city', label: 'Şehir' }, { key: 'delivered', label: 'Teslimat' }, { key: 'cancelled', label: 'İptal' }, { key: 'total_km', label: 'Toplam KM', render: (v: number) => `${formatNumber(Math.round(v))} km` }, { key: 'total_revenue', label: 'Gelir', render: (v: number) => formatCurrency(v) }, { key: 'avg_tl_km', label: 'TL/KM', render: (v: number) => formatCurrency(Math.round(v)) }, { key: 'rating', label: 'Puan', render: (v: any) => Number(v).toFixed(1) }]} onClose={() => setDrillDown(null)} />)}
    </div>
  );
}

function HarcirahReport({ dateRange, onDataLoaded }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [previewDriver, setPreviewDriver] = useState<any>(null);

  const resolvedRange = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) return dateRange;
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await driverService.getInternalHarcirahKmReport(resolvedRange);
      const payload = response?.data ?? null;
      if (response?.success || payload) {
        setData(payload || response);
        onDataLoaded?.(payload || response);
      } else {
        setData(null);
      }
    } catch (e) {
      toast.error('Harcırah raporu yüklenemedi');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [onDataLoaded, resolvedRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Harcırah verisi bulunamadi" />;

  const rows = Array.isArray(data.rows) ? data.rows : [];
  const summary = data.summary || {};
  const topDriver = [...rows].sort((a: any, b: any) => Number(b.totalKm || 0) - Number(a.totalKm || 0))[0];

  if (rows.length === 0) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          Tarih araligi: <strong>{resolvedRange.startDate}</strong> - <strong>{resolvedRange.endDate}</strong>
        </div>
        <EmptyReportState label="Seçilen tarih aralığında özmal sürücü harcırah kaydı bulunamadı" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        Tarih araligi: <strong>{resolvedRange.startDate}</strong> - <strong>{resolvedRange.endDate}</strong>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard title="Özmal Sürücü" value={formatNumber(Number(summary.driverCount || rows.length))} icon={Users} color="#0F766E" />
        <StatCard title="Toplam KM" value={`${formatNumber(Math.round(Number(summary.totalKm || 0)))} km`} icon={Truck} color="#0F766E" />
        <StatCard title="Toplam Sevkiyat" value={formatNumber(Number(summary.totalShipmentCount || 0))} icon={CheckCircle} color="#3B82F6" />
        <StatCard title="Seyir KM" value={summary.totalSeferkm ? `${formatNumber(Math.round(Number(summary.totalSeferkm)))} km` : '-'} icon={TrendingUpIcon} color="#10B981" />
        <StatCard title="Yük Ortağım KM" value={summary.totalYukOrtagimKm ? `${formatNumber(Math.round(Number(summary.totalYukOrtagimKm)))} km` : '-'} icon={Activity} color="#F59E0B" />
        <StatCard title="Workiom KM" value={summary.totalWorkiomKm ? `${formatNumber(Math.round(Number(summary.totalWorkiomKm)))} km` : '-'} icon={Target} color="#8B5CF6" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Özmal Sürücü Harcırah Tablosu</h3>
        <div className="overflow-x-auto pb-2">
          <table className="w-full min-w-[1300px] text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">Sürücü</th>
                <th className="text-left p-3 font-medium text-gray-600">Harcırah Türü</th>
                <th className="text-center p-3 font-medium text-gray-600">Aylık Sabit Maaş</th>
                <th className="text-left p-3 font-medium text-gray-600">İkamet</th>
                <th className="text-left p-3 font-medium text-gray-600">Çekici</th>
                <th className="text-left p-3 font-medium text-gray-600">Dorse</th>
                <th className="text-center p-3 font-medium text-gray-600">Sevkiyat</th>
                <th className="text-center p-3 font-medium text-gray-600">Seyir KM</th>
                <th className="text-center p-3 font-medium text-gray-600">KM Primi</th>
                <th className="text-center p-3 font-medium text-gray-600">Yük Ortağım KM</th>
                <th className="text-center p-3 font-medium text-gray-600">Workiom KM</th>
                <th className="text-center p-3 font-medium text-gray-600">Ara Sefer Sayısı</th>
                <th className="text-center p-3 font-medium text-gray-600">Konaklama Gece</th>
                <th className="text-center p-3 font-medium text-gray-600">Bekleme Bedeli</th>
                <th className="text-center p-3 font-medium text-gray-600">200km Altı Sefer</th>
                <th className="text-center p-3 font-medium text-gray-600">200km Altı Prim</th>
                <th className="text-center p-3 font-medium text-gray-600">Hammaliye</th>
                <th className="text-center p-3 font-medium text-gray-600">Toplam Harcırah</th>
                <th className="text-center p-3 font-medium text-gray-600">Kesintiler(-)</th>
                <th className="text-center p-3 font-medium text-gray-600">AGİ(+)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, i: number) => (
                <tr key={row.driverId || `${row.driverName}-${i}`} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 font-medium text-gray-900">{row.driverName || '-'}</td>
                  <td className="p-3 text-gray-500">{row.harcirahTuru || '-'}</td>
                  <td className="p-3 text-center">{row.aylikSabitMaas ? formatCurrency(Number(row.aylikSabitMaas || 0)) : '-'}</td>
                  <td className="p-3 text-gray-500">{row.residenceCity || '-'}</td>
                  <td className="p-3 text-gray-500">{row.plate || '-'}</td>
                  <td className="p-3 text-gray-500">{row.dorsePlaka || '-'}</td>
                  <td className="p-3 text-center">{formatNumber(Number(row.shipmentCount || 0))}</td>
                  <td className="p-3 text-center">
                    {row.seferkm ? (
                      <button
                        onClick={() => setPreviewDriver(row)}
                        className="font-semibold text-slate-900 underline decoration-dotted underline-offset-2 hover:text-blue-600 transition-colors"
                      >
                        {formatNumber(Math.round(Number(row.seferkm)))} km
                      </button>
                    ) : '-'}
                  </td>
                  <td className="p-3 text-center">{row.kmPrimi ? formatCurrency(Number(row.kmPrimi || 0)) : '-'}</td>
                  <td className="p-3 text-center">{row.yukOrtagimKm ? `${formatNumber(Math.round(Number(row.yukOrtagimKm)))} km` : '-'}</td>
                  <td className="p-3 text-center">{row.workiomKm ? `${formatNumber(Math.round(Number(row.workiomKm)))} km` : '-'}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{formatNumber(Number(row.transferCount || 0))}</span></td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{formatNumber(Number(row.waitingNightCount || 0))}</span></td>
                  <td className="p-3 text-center">{row.waitingFee ? formatCurrency(Number(row.waitingFee || 0)) : '-'}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">{formatNumber(Number(row.shortDistanceCount || 0))}</span></td>
                  <td className="p-3 text-center">{row.shortDistancePrim ? formatCurrency(Number(row.shortDistancePrim || 0)) : '-'}</td>
                  <td className="p-3 text-center">{row.hamaliyeFee ? formatCurrency(Number(row.hamaliyeFee || 0)) : '-'}</td>
                  <td className="p-3 text-center font-semibold text-gray-900">{row.toplamHarcirah ? formatCurrency(Number(row.toplamHarcirah || 0)) : '-'}</td>
                  <td className="p-3 text-center text-red-600">{row.kesintiler ? `-${formatCurrency(Number(row.kesintiler || 0))}` : '-'}</td>
                  <td className="p-3 text-center text-emerald-600">{row.agi ? `+${formatCurrency(Number(row.agi || 0))}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewDriver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{previewDriver.driverName}</h2>
                <p className="text-sm text-gray-500">
                  {previewDriver.plate} {previewDriver.dorsePlaka !== '-' ? `/ ${previewDriver.dorsePlaka}` : ''} &middot; {previewDriver.shipmentCount} sevkiyat
                  {previewDriver.workiomData?.trailerPlate ? ` &middot; Workiom Dorse: ${previewDriver.workiomData.trailerPlate}` : ''}
                </p>
                <div className="flex gap-3 mt-1 text-xs text-gray-500">
                  <span>Seyir: <strong>{formatNumber(Math.round(Number(previewDriver.seferkm || 0)))} km</strong></span>
                  <span>Yük Ortağım: <strong>{formatNumber(Math.round(Number(previewDriver.yukOrtagimKm || 0)))} km</strong></span>
                  <span>Workiom: <strong>{formatNumber(Math.round(Number(previewDriver.workiomKm || 0)))} km</strong></span>
                </div>
              </div>
              <button onClick={() => setPreviewDriver(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-auto flex-1 p-4 space-y-4">
              {/* Workiom Seyir Özeti */}
              {previewDriver.workiomData && (
                <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                  <h4 className="text-sm font-bold text-blue-800 mb-2">Workiom Seyir Verisi ({previewDriver.plate})</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-blue-600">KM:</span> <strong>{formatNumber(Math.round(Number(previewDriver.workiomData.km)))} km</strong></div>
                    <div><span className="text-blue-600">Yakıt:</span> <strong>{formatNumber(Math.round(Number(previewDriver.workiomData.totalFuel)))} L</strong></div>
                    <div><span className="text-blue-600">Sürüş:</span> <strong>{Math.round(Number(previewDriver.workiomData.driveTime) / 3600)} saat</strong></div>
                    <div><span className="text-blue-600">Çalışma:</span> <strong>{Math.round(Number(previewDriver.workiomData.workTime) / 3600)} saat</strong></div>
                  </div>
                </div>
              )}

              {(previewDriver.workiomShipments || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-purple-700 mb-2">Canli Workiom Sevk Detaylari</h4>
                  <div className="overflow-x-auto pb-2">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-purple-50">
                          <th className="text-left p-3 font-medium text-gray-600">Sevk Kodu</th>
                          <th className="text-left p-3 font-medium text-gray-600">Sipariş</th>
                          <th className="text-left p-3 font-medium text-gray-600">Yükleme</th>
                          <th className="text-left p-3 font-medium text-gray-600">Boşaltma</th>
                          <th className="text-left p-3 font-medium text-gray-600">Pickup Date</th>
                          <th className="text-left p-3 font-medium text-gray-600">Teslim Tarihi</th>
                          <th className="text-left p-3 font-medium text-gray-600">Süre</th>
                          <th className="text-center p-3 font-medium text-gray-600">Konaklama Gece</th>
                          <th className="text-center p-3 font-medium text-gray-600">KM</th>
                          <th className="text-center p-3 font-medium text-gray-600">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(previewDriver.workiomShipments || []).map((s: any, idx: number) => (
                          <tr key={`${s.shipmentNo || 'wk'}-${idx}`} className={`border-b ${idx % 2 === 0 ? '' : 'bg-purple-50/30'}`}>
                            <td className="p-3 font-mono text-purple-700 text-xs">{s.shipmentNo || '-'}</td>
                            <td className="p-3 text-gray-700">{s.orderCode || '-'}</td>
                            <td className="p-3 text-gray-700">{[s.pickupCity, s.pickupDistrict].filter(Boolean).join(' / ') || '-'}</td>
                            <td className="p-3 text-gray-700">{[s.deliveryCity, s.deliveryDistrict].filter(Boolean).join(' / ') || '-'}</td>
                            <td className="p-3 text-gray-700 whitespace-nowrap">{formatDateTime(s.pickupDate)}</td>
                            <td className="p-3 text-gray-700 whitespace-nowrap">{formatDateTime(s.deliveryDateCandidate)}</td>
                            <td className="p-3 text-gray-700 whitespace-nowrap">{formatDurationParts(s.durationSeconds)}</td>
                            <td className="p-3 text-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{formatNumber(Number(s.overnightCount || 0))}</span></td>
                            <td className="p-3 text-center">{s.distanceKm ? `${formatNumber(Math.round(s.distanceKm))} km` : '-'}</td>
                            <td className="p-3 text-center">{s.workiomStatusLabel || s.processStatus || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sistem Sevkiyat Detayları */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-2">Sistem Sevkiyat Detayları</h4>
                <div className="overflow-x-auto pb-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-medium text-gray-600">Sevk Kodu</th>
                        <th className="text-left p-3 font-medium text-gray-600">Alış</th>
                        <th className="text-left p-3 font-medium text-gray-600">Boşaltma</th>
                        <th className="text-center p-3 font-medium text-gray-600">KM</th>
                        <th className="text-right p-3 font-medium text-gray-600">Navlun</th>
                        <th className="text-center p-3 font-medium text-gray-600">Kaynak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(previewDriver.shipments || []).length === 0 ? (
                        <tr><td colSpan={6} className="p-8 text-center text-gray-400">Sefer detayı bulunamadı</td></tr>
                      ) : (previewDriver.shipments || []).map((s: any, idx: number) => (
                        <tr key={s.shipmentNo || idx} className={`border-b ${idx % 2 === 0 ? '' : 'bg-gray-50'} ${s.source === 'workiom' ? 'bg-purple-50/50' : ''}`}>
                          <td className="p-3 font-mono text-blue-600 text-xs">{s.shipmentNo || '-'}</td>
                          <td className="p-3 text-gray-700">{s.pickupCity || '-'}</td>
                          <td className="p-3 text-gray-700">{s.deliveryCity || '-'}</td>
                          <td className="p-3 text-center">{s.distanceKm ? `${formatNumber(Math.round(s.distanceKm))} km` : '-'}</td>
                          <td className="p-3 text-right font-medium">{s.finalPrice ? formatCurrency(s.finalPrice) : '-'}</td>
                          <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.source === 'workiom' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{s.source === 'workiom' ? 'Workiom' : 'Sistem'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== VEHICLE PERFORMANCE =====================
function VehiclePerformanceReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/vehicle-performance?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Araç raporu yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Arac performans verisi bulunamadi" />;
  const { vehicleStats, vehicleTypeDistribution, locationDistribution } = data;
  const statusBadge = (status: string) => {
    const map: Record<string, string> = { 'active': 'bg-green-100 text-green-700', 'maintenance': 'bg-yellow-100 text-yellow-700', 'inactive': 'bg-gray-100 text-gray-600' };
    const labels: Record<string, string> = { 'active': 'Aktif', 'maintenance': 'Bakımda', 'inactive': 'Pasif' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || ''}`}>{labels[status] || status}</span>;
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Araç" value={formatNumber(vehicleStats.length)} icon={Truck} color="#F59E0B" />
        <StatCard title="Aktif Araç" value={formatNumber(vehicleStats.filter((v: any) => v.status === 'active').length)} icon={CheckCircle} color="#10B981" />
        <StatCard title="Toplam KM" value={`${formatNumber(Math.round(vehicleStats.reduce((s: number, v: any) => s + v.total_km, 0)))} km`} icon={MapPin} color="#3B82F6" />
        <StatCard title="Çalışan Motor" value={formatNumber(vehicleStats.filter((v: any) => v.is_vehicle_running).length)} icon={Activity} color="#EF4444" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Araç Tipi Dağılımı</h3>
          <ResponsiveContainer width="100%" height={280}><PieChart><Pie data={vehicleTypeDistribution} cx="50%" cy="50%" outerRadius={90} label={({ vehicle_type, percent }) => `${vehicle_type} ${(percent * 100).toFixed(0)}%`} dataKey="count">{vehicleTypeDistribution.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Konum Dağılımı (Top 10)</h3>
          <ResponsiveContainer width="100%" height={280}><BarChart data={locationDistribution} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis type="number" /><YAxis type="category" dataKey="current_address" width={200} tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="vehicle_count" fill="#F59E0B" name="Araç Sayısı" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Araç Performans Tablosu</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Plaka</th><th className="text-left p-3 font-medium text-gray-600">Marka/Model</th><th className="text-center p-3 font-medium text-gray-600">Durum</th><th className="text-center p-3 font-medium text-gray-600">Konum</th><th className="text-center p-3 font-medium text-gray-600">Günlük KM</th><th className="text-center p-3 font-medium text-gray-600">Yakıt</th><th className="text-center p-3 font-medium text-gray-600">Toplam Sefer</th><th className="text-center p-3 font-medium text-gray-600">Toplam KM</th><th className="text-center p-3 font-medium text-gray-600">Gelir</th></tr></thead>
            <tbody>
              {vehicleStats.map((v: any, i: number) => (
                <tr key={v.plate} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 font-mono font-medium text-gray-900">{v.plate}</td>
                  <td className="p-3 text-gray-500">{v.brand} {v.model}</td>
                  <td className="p-3 text-center">{statusBadge(v.status)}</td>
                  <td className="p-3 text-xs text-gray-500 max-w-[200px] truncate" title={v.current_address}>{v.current_address?.substring(0, 40) || '-'}</td>
                  <td className="p-3 text-center">{v.daily_km ? `${v.daily_km} km` : '-'}</td>
                  <td className="p-3 text-center">{v.fuel_level ? (<div className="flex items-center gap-1 justify-center"><div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v.fuel_level}%`, backgroundColor: v.fuel_level > 50 ? '#10B981' : v.fuel_level > 20 ? '#F59E0B' : '#EF4444' }} /></div><span className="text-xs">{v.fuel_level}%</span></div>) : '-'}</td>
                  <td className="p-3 text-center">{formatNumber(v.total_shipments)}</td>
                  <td className="p-3 text-center">{formatNumber(Math.round(v.total_km))} km</td>
                  <td className="p-3 text-center font-medium">{formatCurrency(v.total_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===================== CUSTOMER ANALYSIS =====================
function CustomerAnalysisReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/customer-analysis?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Müşteri analizi yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Musteri analizi verisi bulunamadi" />;
  const { topCustomers, cityDistribution, customerTypeDistribution } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Müşteri" value={formatNumber(topCustomers.length)} icon={Users} color="#EC4899" />
        <StatCard title="En Aktif Müşteri" value={topCustomers[0]?.company_name || '-'} subtitle={`${formatNumber(topCustomers[0]?.shipment_count || 0)} sefer`} icon={Award} color="#F59E0B" />
        <StatCard title="Şehir Sayısı" value={formatNumber(cityDistribution.length)} icon={MapPin} color="#3B82F6" />
        <StatCard title="Yük. Müşteri" value={formatNumber(topCustomers.filter((c: any) => c.customer_type === 'loading').length)} icon={Truck} color="#10B981" />
      </div>
      <CityHeatmap data={cityDistribution.map((c: any) => ({ name: c.city, value: c.customer_count }))} title="Şehir Dağılımı (Müşteri)" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Müşteri Tipi Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}><PieChart><Pie data={customerTypeDistribution} cx="50%" cy="50%" outerRadius={90} label={({ customer_type, percent }) => `${customer_type === 'loading' ? 'Yükleme' : customer_type === 'delivery' ? 'Boşaltma' : 'Her İkisi'} ${(percent * 100).toFixed(0)}%`} dataKey="count">{customerTypeDistribution.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Şehir Bazlı Müşteri Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={cityDistribution.slice(0, 10)}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="city" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Bar dataKey="customer_count" fill="#EC4899" name="Müşteri Sayısı" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">En Aktif Müşteriler</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">#</th><th className="text-left p-3 font-medium text-gray-600">Müşteri</th><th className="text-left p-3 font-medium text-gray-600">Şehir</th><th className="text-center p-3 font-medium text-gray-600">Tip</th><th className="text-center p-3 font-medium text-gray-600">Sefer</th><th className="text-center p-3 font-medium text-gray-600">Gelir</th><th className="text-center p-3 font-medium text-gray-600">Ort. Değer</th></tr></thead>
            <tbody>
              {topCustomers.map((c: any, i: number) => (
                <tr key={c.company_name} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 text-gray-400">{i + 1}</td>
                  <td className="p-3 font-medium text-gray-900">{c.company_name}</td>
                  <td className="p-3 text-gray-500">{c.city} / {c.district}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.customer_type === 'loading' ? 'bg-blue-100 text-blue-700' : c.customer_type === 'delivery' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>{c.customer_type === 'loading' ? 'Yükleme' : c.customer_type === 'delivery' ? 'Boşaltma' : 'Her İkisi'}</span></td>
                  <td className="p-3 text-center font-medium">{formatNumber(c.shipment_count)}</td>
                  <td className="p-3 text-center">{formatCurrency(c.total_revenue)}</td>
                  <td className="p-3 text-center">{formatCurrency(Math.round(c.avg_shipment_value))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===================== ROUTE ANALYSIS =====================
function RouteAnalysisReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/route-analysis?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Güzergah analizi yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Guzergah analizi verisi bulunamadi" />;
  const { topRoutes, distanceDistribution } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Rota" value={formatNumber(topRoutes.length)} icon={MapPin} color="#06B6D4" />
        <StatCard title="En Popüler Rota" value={topRoutes[0]?.route || '-'} subtitle={`${formatNumber(topRoutes[0]?.shipment_count || 0)} sefer`} icon={TrendingUp} color="#F59E0B" />
        <StatCard title="En Gelirli Rota" value={formatCurrency(topRoutes[0]?.total_revenue || 0)} icon={DollarSign} color="#10B981" />
        <StatCard title="Ort. TL/KM" value={formatCurrency(Math.round(topRoutes[0]?.avg_tl_km || 0))} icon={Activity} color="#8B5CF6" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">En Popüler Güzergahlar</h3>
          <ResponsiveContainer width="100%" height={350}><BarChart data={topRoutes.slice(0, 10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis type="number" /><YAxis type="category" dataKey="route" width={180} tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="shipment_count" fill="#06B6D4" name="Sefer" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Mesafe Dağılımı</h3>
          <ResponsiveContainer width="100%" height={350}><BarChart data={distanceDistribution}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="distance_range" /><YAxis /><Tooltip /><Legend /><Bar dataKey="count" fill="#06B6D4" name="Sefer Sayısı" radius={[4, 4, 0, 0]} /><Bar dataKey="avg_price" fill="#F59E0B" name="Ort. Fiyat" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rota Detay Tablosu</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Rota</th><th className="text-center p-3 font-medium text-gray-600">Sefer</th><th className="text-center p-3 font-medium text-gray-600">Ort. Mesafe</th><th className="text-center p-3 font-medium text-gray-600">Ort. Fiyat</th><th className="text-center p-3 font-medium text-gray-600">Toplam Gelir</th><th className="text-center p-3 font-medium text-gray-600">TL/KM</th></tr></thead>
            <tbody>
              {topRoutes.map((r: any, i: number) => (
                <tr key={r.route} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 font-medium text-gray-900">{r.route}</td>
                  <td className="p-3 text-center">{formatNumber(r.shipment_count)}</td>
                  <td className="p-3 text-center">{Math.round(r.avg_distance)} km</td>
                  <td className="p-3 text-center">{formatCurrency(Math.round(r.avg_price))}</td>
                  <td className="p-3 text-center font-medium">{formatCurrency(r.total_revenue)}</td>
                  <td className="p-3 text-center">{formatCurrency(Math.round(r.avg_tl_km))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===================== BID ANALYSIS =====================
function BidAnalysisReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/bid-analysis?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Teklif analizi yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Teklif analizi verisi bulunamadi" />;
  const { bidStats, bidTrend, driverBidStats } = data;
  const acceptanceRate = bidStats.total_bids > 0 ? ((bidStats.accepted / bidStats.total_bids) * 100).toFixed(1) : '0';
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Toplam Teklif" value={formatNumber(bidStats.total_bids)} icon={FileText} color="#F97316" />
        <StatCard title="Bekleyen" value={formatNumber(bidStats.pending)} icon={Clock} color="#F59E0B" />
        <StatCard title="Kabul Edilen" value={formatNumber(bidStats.accepted)} icon={CheckCircle} color="#10B981" />
        <StatCard title="Reddedilen" value={formatNumber(bidStats.rejected)} icon={XCircle} color="#EF4444" />
        <StatCard title="Kabul Oranı" value={`${acceptanceRate}%`} icon={Target} color="#8B5CF6" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Teklif Trendi (30 Gün)</h3>
          <ResponsiveContainer width="100%" height={300}><LineChart data={[...bidTrend].reverse()}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Legend /><Line type="monotone" dataKey="count" stroke="#F97316" strokeWidth={2} name="Toplam" /><Line type="monotone" dataKey="accepted" stroke="#10B981" strokeWidth={2} name="Kabul" /></LineChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Teklif Durumu</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Bekliyor', value: bidStats.pending, color: '#F59E0B' },
                  { name: 'Kabul', value: bidStats.accepted, color: '#10B981' },
                  { name: 'Red', value: bidStats.rejected, color: '#EF4444' }
                ]}
                cx="50%"
                cy="50%"
                outerRadius={90}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                dataKey="value"
              >
                {[
                  { name: 'Bekliyor', value: bidStats.pending, color: '#F59E0B' },
                  { name: 'Kabul', value: bidStats.accepted, color: '#10B981' },
                  { name: 'Red', value: bidStats.rejected, color: '#EF4444' }
                ].map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">En Aktif Teklif Verenler</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Sürücü</th><th className="text-center p-3 font-medium text-gray-600">Toplam Teklif</th><th className="text-center p-3 font-medium text-gray-600">Kabul</th><th className="text-center p-3 font-medium text-gray-600">Ort. Teklif</th></tr></thead>
            <tbody>
              {driverBidStats.map((d: any, i: number) => (
                <tr key={i} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 font-medium text-gray-900">{d.driver_name}</td>
                  <td className="p-3 text-center">{formatNumber(d.total_bids)}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">{d.accepted}</span></td>
                  <td className="p-3 text-center">{formatCurrency(Math.round(d.avg_bid))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===================== ON-TIME DELIVERY =====================
function OnTimeDeliveryReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/on-time-delivery?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Zamanında teslimat raporu yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Zamaninda teslimat verisi bulunamadi" />;
  const { onTimeStats, lateDeliveries } = data;
  const onTimeRate = toNumeric(onTimeStats.total_delivered) > 0 ? ((toNumeric(onTimeStats.on_time) / toNumeric(onTimeStats.total_delivered)) * 100).toFixed(1) : '0';
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Teslim Edilen" value={formatNumber(toNumeric(onTimeStats.total_delivered))} icon={CheckCircle} color="#3B82F6" />
        <StatCard title="Zamanında" value={`${onTimeRate}%`} subtitle={`${formatNumber(toNumeric(onTimeStats.on_time))} teslimat`} icon={Award} color="#10B981" />
        <StatCard title="Geciken" value={formatNumber(toNumeric(onTimeStats.late))} icon={AlertTriangle} color="#EF4444" />
        <StatCard title="Ort. Teslimat" value={`${toNumeric(onTimeStats.avg_delivery_hours).toFixed(1)} saat`} icon={Clock} color="#8B5CF6" />
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Zamanında Teslimat Oranı</h3>
        <div className="flex items-center gap-8">
          <div className="relative w-40 h-40">
            <svg className="w-40 h-40 transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#10B981" strokeWidth="8" strokeDasharray={`${parseFloat(onTimeRate) * 2.51} 251.2`} strokeLinecap="round" />
            </svg>
             <div className="absolute inset-0 flex items-center justify-center"><span className="text-2xl font-bold text-gray-900">{onTimeRate}%</span></div>
          </div>
          <div className="space-y-3 flex-1">
             <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-green-500" /><span className="text-sm text-gray-600">Zamanında: <strong>{formatNumber(toNumeric(onTimeStats.on_time))}</strong></span></div>
             <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-red-500" /><span className="text-sm text-gray-600">Geciken: <strong>{formatNumber(toNumeric(onTimeStats.late))}</strong></span></div>
             <div className="flex items-center gap-3"><div className="w-4 h-4 rounded-full bg-gray-400" /><span className="text-sm text-gray-600">Süresiz: <strong>{formatNumber(toNumeric(onTimeStats.no_deadline))}</strong></span></div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Geciken Teslimatlar</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Sevk Kodu</th><th className="text-left p-3 font-medium text-gray-600">Rota</th><th className="text-left p-3 font-medium text-gray-600">Sürücü</th><th className="text-left p-3 font-medium text-gray-600">Plaka</th><th className="text-center p-3 font-medium text-gray-600">Beklenen</th><th className="text-center p-3 font-medium text-gray-600">Gerçekleşen</th><th className="text-center p-3 font-medium text-gray-600">Gecikme</th></tr></thead>
            <tbody>
              {lateDeliveries.map((d: any, i: number) => (
                <tr key={d.shipment_no} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 font-mono text-blue-600">{d.shipment_no}</td>
                  <td className="p-3 text-gray-500">{d.pickup_city} → {d.delivery_city}</td>
                  <td className="p-3 text-gray-900">{d.driver_name}</td>
                  <td className="p-3 font-mono">{d.cekici_plaka}</td>
                  <td className="p-3 text-center">{d.expected_date ? new Date(d.expected_date).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="p-3 text-center">{d.actual_date ? new Date(d.actual_date).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">+{Math.round(d.late_hours)} saat</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===================== ACCOUNTING REPORT =====================
function AccountingReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/accounting?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Muhasebe raporu yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Muhasebe raporu verisi bulunamadi" />;
  const { accountingSummary, pendingInvoices, monthlyInvoices } = data;
  const statusMap: Record<string, { label: string; color: string }> = {
    'none': { label: 'Fatura Yok', color: 'bg-gray-100 text-gray-600' },
    'invoice_uploaded': { label: 'Fatura Yüklendi', color: 'bg-blue-100 text-blue-700' },
    'invoice_approved': { label: 'Fatura Onaylandı', color: 'bg-purple-100 text-purple-700' },
    'payment_pending': { label: 'Ödeme Bekliyor', color: 'bg-yellow-100 text-yellow-700' },
    'payment_completed': { label: 'Ödeme Tamamlandı', color: 'bg-green-100 text-green-700' },
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Fatura Bekliyor" value={formatNumber(accountingSummary.no_invoice)} icon={FileText} color="#6B7280" />
        <StatCard title="Fatura Yüklendi" value={formatNumber(accountingSummary.invoice_uploaded)} icon={FileText} color="#3B82F6" />
        <StatCard title="Onaylandı" value={formatNumber(accountingSummary.invoice_approved)} icon={CheckCircle} color="#8B5CF6" />
        <StatCard title="Ödeme Bekliyor" value={formatNumber(accountingSummary.payment_pending)} icon={Clock} color="#F59E0B" />
        <StatCard title="Ödeme Tamam" value={formatNumber(accountingSummary.payment_completed)} icon={DollarSign} color="#10B981" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fatura Durumu</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Fatura Yok', value: accountingSummary.no_invoice, color: '#6B7280' },
                  { name: 'Yüklendi', value: accountingSummary.invoice_uploaded, color: '#3B82F6' },
                  { name: 'Onaylandı', value: accountingSummary.invoice_approved, color: '#8B5CF6' },
                  { name: 'Ödeme Bekliyor', value: accountingSummary.payment_pending, color: '#F59E0B' },
                  { name: 'Tamamlandı', value: accountingSummary.payment_completed, color: '#10B981' }
                ]}
                cx="50%"
                cy="50%"
                outerRadius={90}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                dataKey="value"
              >
                {[{ color: '#6B7280' }, { color: '#3B82F6' }, { color: '#8B5CF6' }, { color: '#F59E0B' }, { color: '#10B981' }].map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Aylık Fatura Trendi</h3>
          <ResponsiveContainer width="100%" height={280}><BarChart data={[...monthlyInvoices].reverse()}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}K`} /><Tooltip formatter={(v: number) => formatCurrency(v)} /><Legend /><Bar dataKey="invoice_total" fill="#6366F1" name="Fatura Tutarı" radius={[4, 4, 0, 0]} /><Bar dataKey="paid_count" fill="#10B981" name="Ödenen Adet" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bekleyen Faturalar</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">Sevk Kodu</th><th className="text-left p-3 font-medium text-gray-600">Rota</th><th className="text-left p-3 font-medium text-gray-600">Sürücü</th><th className="text-center p-3 font-medium text-gray-600">Navlun</th><th className="text-center p-3 font-medium text-gray-600">Fatura Tutarı</th><th className="text-center p-3 font-medium text-gray-600">Fatura Kodu</th><th className="text-center p-3 font-medium text-gray-600">Durum</th></tr></thead>
            <tbody>
              {pendingInvoices.map((s: any, i: number) => (
                <tr key={s.shipment_no} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="p-3 font-mono text-blue-600">{s.shipment_no}</td>
                  <td className="p-3 text-gray-500">{s.pickup_city} → {s.delivery_city}</td>
                  <td className="p-3 text-gray-900">{s.driver_name}</td>
                  <td className="p-3 text-center">{formatCurrency(s.final_price)}</td>
                  <td className="p-3 text-center">{s.invoice_amount ? formatCurrency(s.invoice_amount) : '-'}</td>
                  <td className="p-3 text-center font-mono text-xs">{s.invoice_code || '-'}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${(statusMap[s.accounting_status] || statusMap['none']).color}`}>{(statusMap[s.accounting_status] || statusMap['none']).label}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===================== WAITING TIME REPORT =====================
function Hourglass(props: any) {
  return (<svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14" /><path d="M5 2h14" /><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" /><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" /></svg>);
}

function WaitingTimeReport({ dateRange }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange).toString();
      const res = await fetch(`${API_BASE}/api/reports/waiting-time?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { toast.error('Bekleme süresi raporu yüklenemedi'); } finally { setLoading(false); }
  }, [dateRange]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <EmptyReportState label="Bekleme suresi verisi bulunamadi" />;
  const { waitingSummary, waitingByLocation, waitingByDriver } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Bekleme" value={formatNumber(waitingSummary.total_waiting_records)} icon={Timer} color="#EF4444" />
        <StatCard title="Ort. Bekleme" value={`${Math.round(waitingSummary.avg_waiting_minutes)} dk`} icon={Clock} color="#F59E0B" />
        <StatCard title="Max Bekleme" value={`${Math.round(waitingSummary.max_waiting_minutes)} dk`} icon={AlertTriangle} color="#EF4444" />
        <StatCard title="Toplam Süre" value={`${Math.round(waitingSummary.total_waiting_minutes / 60)} saat`} icon={Hourglass} color="#8B5CF6" />
      </div>
      <CityHeatmap data={waitingByLocation.map((w: any) => ({ name: w.location, value: Math.round(w.total_waiting_minutes) }))} title="Konuma Göre Toplam Bekleme Süresi (dakika)" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Konuma Göre Ort. Bekleme</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={waitingByLocation.slice(0, 10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis type="number" tickFormatter={(v: number) => `${Math.round(v)} dk`} /><YAxis type="category" dataKey="location" width={120} tick={{ fontSize: 11 }} /><Tooltip formatter={(v: number) => `${Math.round(v)} dk`} /><Bar dataKey="avg_waiting_minutes" fill="#EF4444" name="Ort. Bekleme" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sürücüye Göre Bekleme</h3>
          <ResponsiveContainer width="100%" height={300}><BarChart data={waitingByDriver.slice(0, 10)}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="driver_name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={80} /><YAxis tickFormatter={(v: number) => `${Math.round(v)} dk`} /><Tooltip formatter={(v: number) => `${Math.round(v)} dk`} /><Bar dataKey="avg_waiting_minutes" fill="#F59E0B" name="Ort. Bekleme" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5"><p className="text-sm text-gray-500 mb-1">Onaylanan</p><p className="text-2xl font-bold text-green-600">{formatNumber(waitingSummary.approved)}</p></div>
        <div className="bg-white rounded-xl shadow-sm border p-5"><p className="text-sm text-gray-500 mb-1">Bekleyen</p><p className="text-2xl font-bold text-yellow-600">{formatNumber(waitingSummary.pending)}</p></div>
        <div className="bg-white rounded-xl shadow-sm border p-5"><p className="text-sm text-gray-500 mb-1">Reddedilen</p><p className="text-2xl font-bold text-red-600">{formatNumber(waitingSummary.rejected)}</p></div>
      </div>
    </div>
  );
}

// ===================== CUSTOM REPORT BUILDER =====================
const availableMetrics = [
  { id: 'total_shipments', label: 'Toplam Sevkiyat', table: 'shipments' },
  { id: 'delivered', label: 'Teslim Edilen', table: 'shipments' },
  { id: 'cancelled', label: 'İptal', table: 'shipments' },
  { id: 'in_transit', label: 'Yolda', table: 'shipments' },
  { id: 'total_revenue', label: 'Toplam Gelir', table: 'shipments' },
  { id: 'avg_distance', label: 'Ort. Mesafe', table: 'shipments' },
  { id: 'avg_tl_km', label: 'Ort. TL/KM', table: 'shipments' },
  { id: 'active_drivers', label: 'Aktif Sürücü', table: 'shipments' },
  { id: 'active_vehicles', label: 'Aktif Araç', table: 'shipments' },
  { id: 'total_expenses', label: 'Toplam Gider', table: 'expenses' },
  { id: 'total_waiting', label: 'Toplam Bekleme (dk)', table: 'waiting' },
  { id: 'total_bids', label: 'Toplam Teklif', table: 'bids' },
];

const availableDimensions = [
  { id: 'transport_model', label: 'Taşıma Modeli' },
  { id: 'carrier_type', label: 'Taşıyıcı Tipi' },
  { id: 'load_type', label: 'Yük Türü' },
  { id: 'pickup_city', label: 'Alış Şehri' },
  { id: 'delivery_city', label: 'Boşaltma Şehri' },
  { id: 'status', label: 'Durum' },
  { id: 'driver_name', label: 'Sürücü' },
  { id: 'vehicle_type', label: 'Araç Tipi' },
];

function CustomReportBuilder({ dateRange }: any) {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['total_shipments', 'total_revenue', 'delivered']);
  const [selectedDimension, setSelectedDimension] = useState('transport_model');
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const toggleMetric = (id: string) => setSelectedMetrics(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  const handleGenerate = async () => {
    if (!selectedMetrics.length) { toast.error('En az bir metrik seçin'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ metrics: selectedMetrics.join(','), dimension: selectedDimension, startDate: dateRange.startDate, endDate: dateRange.endDate }).toString();
      const res = await fetch(`${API_BASE}/api/reports/custom?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setReportData(json.data);
    } catch (e) { toast.error('Özel rapor oluşturulamadı'); } finally { setLoading(false); }
  };
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Metrikleri Seçin</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {availableMetrics.map(m => (
            <button key={m.id} onClick={() => toggleMetric(m.id)} className={`px-4 py-3 rounded-lg text-sm font-medium border transition-all ${selectedMetrics.includes(m.id) ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}>
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Boyut (Gruplama)</h3>
        <div className="flex flex-wrap gap-2">
          {availableDimensions.map(d => (
            <button key={d.id} onClick={() => setSelectedDimension(d.id)} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${selectedDimension === d.id ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'}`}>{d.label}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{selectedMetrics.length} metrik seçildi - "{availableDimensions.find(d => d.id === selectedDimension)?.label}" bazında gruplanacak</p>
        <button onClick={handleGenerate} disabled={loading} className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Rapor Oluştur
        </button>
      </div>
      {reportData && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Grafiksel Görünüm</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={reportData.rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="dimension" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                {selectedMetrics.map((m: string, i: number) => (<Bar key={m} dataKey={m} fill={COLORS[i % COLORS.length]} name={availableMetrics.find(am => am.id === m)?.label || m} radius={[4, 4, 0, 0]} />))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tablo Görünümü</h3>
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50"><th className="text-left p-3 font-medium text-gray-600">{availableDimensions.find(d => d.id === selectedDimension)?.label}</th>{selectedMetrics.map(m => (<th key={m} className="text-center p-3 font-medium text-gray-600">{availableMetrics.find(am => am.id === m)?.label}</th>))}</tr></thead>
              <tbody>
                {reportData.rows.map((row: any, i: number) => (<tr key={i} className={`border-b ${i % 2 === 0 ? '' : 'bg-gray-50'}`}><td className="p-3 font-medium text-gray-900">{row.dimension}</td>{selectedMetrics.map(m => (<td key={m} className="p-3 text-center">{m.includes('revenue') || m.includes('expenses') || m.includes('tl_km') ? formatCurrency(row[m]) : formatNumber(row[m])}</td>))}</tr>))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== MAIN REPORTS PAGE =====================
export function Reports() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeReport, setActiveReport] = useState(searchParams.get('tab') || 'dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  });
  const [customDate, setCustomDate] = useState(true);
  const [rawData, setRawData] = useState<any>(null);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef<any>(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveReport(tab);
  }, [searchParams]);

  const ActiveIcon = reportCategories.find(r => r.id === activeReport)?.icon || LayoutDashboard;

  const handleNavigate = (tab: string) => {
    setActiveReport(tab);
    setRawData(null);
    navigate(`/reports?tab=${tab}`);
  };

  const handleExport = async () => {
    if (!rawData) { toast.error('Önce bir rapor yükleyin'); return; }
    setExporting(true);
    try {
      const exportData = prepareExportData(activeReport, rawData);
      if (!exportData.length) { toast.error('Dışa aktarılacak veri yok'); return; }
      exportToExcel(exportData, `${activeReport}_rapor_${new Date().toISOString().slice(0, 10)}`, activeReport);
      toast.success('Excel dosyası indirildi');
    } catch (e) { toast.error('Excel dışa aktarma hatası'); } finally { setExporting(false); }
  };

  const captureRawData = useCallback((data: any) => setRawData(data), []);

  const renderReport = () => {
    switch (activeReport) {
      case 'dashboard': return <DashboardSummary dateRange={dateRange} />;
      case 'comparison': return <ComparisonReport dateRange={dateRange} />;
      case 'predictive': return <PredictiveAnalytics dateRange={dateRange} />;
      case 'operation': return <OperationSummary dateRange={dateRange} />;
      case 'financial': return <FinancialReport dateRange={dateRange} />;
      case 'driver': return <DriverPerformanceReport dateRange={dateRange} />;
      case 'harcirah': return <HarcirahReport dateRange={dateRange} onDataLoaded={captureRawData} />;
      case 'vehicle': return <VehiclePerformanceReport dateRange={dateRange} />;
      case 'customer': return <CustomerAnalysisReport dateRange={dateRange} />;
      case 'route': return <RouteAnalysisReport dateRange={dateRange} />;
      case 'bid': return <BidAnalysisReport dateRange={dateRange} />;
      case 'ontime': return <OnTimeDeliveryReport dateRange={dateRange} />;
      case 'accounting': return <AccountingReport dateRange={dateRange} />;
      case 'waiting': return <WaitingTimeReport dateRange={dateRange} />;
      case 'custom': return <FlexibleReportsBuilder />;
      default: return null;
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 bg-white rounded-xl shadow-sm border flex flex-col shrink-0`}>
        <div className="p-4 border-b flex items-center justify-between">
          {sidebarOpen && <h2 className="font-semibold text-gray-900">Raporlar</h2>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 hover:bg-gray-100 rounded-lg shrink-0"><BarChart3 className="w-5 h-5 text-gray-600" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {reportCategories.map((cat) => {
            const Icon = cat.icon;
            const isNew = ['dashboard', 'comparison', 'predictive', 'custom'].includes(cat.id);
            return (
              <button
                key={cat.id}
                onClick={() => handleNavigate(cat.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeReport === cat.id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                style={activeReport === cat.id ? { backgroundColor: cat.color } : {}}
                title={!sidebarOpen ? cat.name : ''}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {sidebarOpen && (
                  <div className="flex items-center gap-1 truncate">
                    <span className="truncate">{cat.name}</span>
                    {isNew && <span className="text-[9px] px-1.5 py-0.5 bg-white/20 rounded-full shrink-0">YENİ</span>}
                  </div>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${reportCategories.find(r => r.id === activeReport)?.color}25` }}>
                <ActiveIcon className="w-6 h-6" style={{ color: reportCategories.find(r => r.id === activeReport)?.color }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{reportCategories.find(r => r.id === activeReport)?.name}</h1>
                <p className="text-sm text-gray-500">Sistem performans analiz raporu</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!['dashboard', 'predictive', 'custom'].includes(activeReport) && (
                <button onClick={handleExport} disabled={exporting} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Excel
                </button>
              )}
              <select
                value={customDate ? 'custom' : dateRange.startDate ? '' : '30days'}
                onChange={(e) => {
                  if (e.target.value === 'custom') { setCustomDate(true); const d = new Date(); d.setDate(d.getDate() - 30); setDateRange({ startDate: d.toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }); }
                  else if (e.target.value === '30days') { setCustomDate(false); setDateRange({ startDate: '', endDate: '' }); }
                  else if (e.target.value === '7days') { setCustomDate(true); const d = new Date(); d.setDate(d.getDate() - 7); setDateRange({ startDate: d.toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }); }
                  else if (e.target.value === '90days') { setCustomDate(true); const d = new Date(); d.setDate(d.getDate() - 90); setDateRange({ startDate: d.toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }); }
                  else if (e.target.value === '365days') { setCustomDate(true); const d = new Date(); d.setDate(d.getDate() - 365); setDateRange({ startDate: d.toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }); }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="30days">Son 30 Gün</option>
                <option value="7days">Son 7 Gün</option>
                <option value="90days">Son 90 Gün</option>
                <option value="365days">Son 1 Yıl</option>
                <option value="custom">Özel Tarih Aralığı</option>
              </select>
              {customDate && (
                <>
                  <input type="date" value={dateRange.startDate} onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <input type="date" value={dateRange.endDate} onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </>
              )}
            </div>
          </div>
        </div>
        {renderReport()}
      </div>
    </div>
  );
}
